import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { type SupportedCountryCode } from '@zep-us/i18n';
import { useTranslation } from 'next-i18next';

import type { CategoryTreeBuildDto } from '@/api/gen/_model/categoryTreeBuildDto';
import { type CategoryTree } from '@/shared/category/type';
import { type SelectionSchoolInfo } from '@/views/build/type';

import { useBuildFormContext } from '../../../form/schema';
import { type CategoryInfoInputType } from '../../category-info/types';
import { generateCategoryLabels } from '../../category-reader/utils';
import { useCategoryTreeConversion } from '../../category-tree';
import { generateQuizTitle } from '../utils';

/**
 * Quiz Title을 카테고리 정보와 동기화하는 Hook
 *
 * 카테고리 선택 완료 시 호출할 동기화 함수를 반환합니다.
 * 사용자가 title을 수동으로 수정한 경우에는 덮어쓰지 않습니다.
 *
 * @example
 * const syncQuizTitleWithCategory = useSyncQuizTitleWithCategory({
 *   schoolInfoTree,
 *   subjectTree,
 *   publishers,
 *   userCountryCode: 'KR',
 * });
 *
 * // 카테고리 모달에서 카테고리 선택 완료 시
 * categoryInfoModal.openAsync({
 *   onComplete: (values) => {
 *     syncQuizTitleWithCategory(values); // 직접 호출
 *   }
 * });
 */
export const useSyncQuizTitleWithCategory = ({
  schoolInfoTree,
  subjectTree,
  publishers,
  userCountryCode,
}: {
  /** 학교 정보 트리 */
  schoolInfoTree: CategoryTree;
  /** 과목 트리 */
  subjectTree: CategoryTreeBuildDto[];
  /** 출판사 정보 */
  publishers?: SelectionSchoolInfo['publishers'];
  /** 사용자 국가 코드 */
  userCountryCode: SupportedCountryCode;
}) => {
  const { setValue, control } = useBuildFormContext();
  const { t } = useTranslation(['build', 'common']);
  const { categoryToMenuItem } = useCategoryTreeConversion();

  // Watch current title and category values (for comparison)
  // Single useWatch call to avoid 8x re-renders on form reset
  const [
    title,
    schoolTypeId,
    gradeId,
    semesterId,
    subject,
    subSubject,
    publisher,
    customPublisher,
    isLastSchoolInfoSaved,
  ] = useWatch({
    control,
    name: [
      'title',
      'schoolTypeId',
      'gradeId',
      'semesterId',
      'subject',
      'subSubject',
      'publisher',
      'customPublisher',
      'isLastSchoolInfoSaved',
    ],
  });

  // Convert subjectTree to CategoryNodes for label generation
  const subjectNodes = useMemo(() => {
    return subjectTree.map(node => categoryToMenuItem(node));
  }, [subjectTree, categoryToMenuItem]);

  // Return the sync function directly
  return useCallback(
    (categoryValues: CategoryInfoInputType) => {
      // 1. 새로운 categoryValues로 새 title 생성
      const newLabels = generateCategoryLabels({
        categoryValues,
        schoolInfoTree,
        subjectNodes,
        publishers: publishers?.[userCountryCode],
        userCountryCode,
        t,
      });

      const newTitle = generateQuizTitle({
        schoolInfoLabel: {
          schoolTypeLabel: newLabels.schoolTypeLabel,
          gradeLabel: newLabels.gradeLabel,
          semesterLabel: newLabels.semesterLabel,
        },
        subjectLabel: newLabels.subjectLabel,
        publisherLabel: newLabels.publisherLabel,
      });

      // 2. 현재 form의 카테고리 정보로 현재 자동생성 title 계산
      const currentCategoryValues = {
        schoolTypeId,
        gradeId,
        semesterId,
        subject,
        subSubject,
        publisher,
        customPublisher,
        isLastSchoolInfoSaved,
      };

      const currentLabels = generateCategoryLabels({
        categoryValues: currentCategoryValues,
        schoolInfoTree,
        subjectNodes,
        publishers: publishers?.[userCountryCode],
        userCountryCode,
        t,
      });

      const currentAutoGeneratedTitle = generateQuizTitle({
        schoolInfoLabel: {
          schoolTypeLabel: currentLabels.schoolTypeLabel,
          gradeLabel: currentLabels.gradeLabel,
          semesterLabel: currentLabels.semesterLabel,
        },
        subjectLabel: currentLabels.subjectLabel,
        publisherLabel: currentLabels.publisherLabel,
      });

      // 3. 업데이트 조건 확인
      const shouldUpdate = !title || title === currentAutoGeneratedTitle;

      // 4. 조건 만족 시에만 title 설정
      if (shouldUpdate && newTitle) {
        setValue('title', newTitle, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    },
    [
      setValue,
      title,
      schoolTypeId,
      gradeId,
      semesterId,
      subject,
      subSubject,
      publisher,
      customPublisher,
      isLastSchoolInfoSaved,
      schoolInfoTree,
      subjectNodes,
      publishers,
      userCountryCode,
      t,
    ],
  );
};
